import { useState, useEffect } from "react";
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next";
import { manageMenuForm } from "../../formSchemas/manageMenu.form";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import { ICallAPIFunction } from "../../yoello-lib/modules/API2/types";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import EColourPanel from "../../yoello-lib/modules/dynamicForms/interfaces/EColourPanel";
import { IMenuData, IMenuFormData, menuTimingList, TimingData } from "./types.d";
import IMenuItemBody from "../../interfaces/IMenuItemBody";
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import useTask from "../../hooks/useTask";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import ISubCategory from "../menu/categories/ISubCategory";
import { MenuTimingArray } from "./types.d";
import IDynamicFormRow from "../../yoello-lib/modules/dynamicForms/interfaces/IDynamicFormRow";
import { TimingMenusContainer } from "./TimingMenusContainer";
import { generateMenuTimingArray, menuTimingInitialState, generateMenuTimingData } from "./utils"
import { IReduxAppState } from "../../reducers";
import { IMultiDialogChildren } from "../dialogRoutes/MultiDialogRoute";
import { useDialogSetup } from "../dialogRoutes/useDialogSetup";
import { useChangedDialog } from "../../hooks/useChangedDialog";
import DisabledActionWrapper from "../../yoello-lib/modules/components/DisabledActionWrapper";
import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";
import { MenuURLLookup } from "./ui/MenuURLLookup";
import { generateConsumerURL } from "../../services/generateConsumerURLs";
import { FormLoadingWrapper } from "../util/FormLoadingWrapper";
import ListLoading from "../util/Loading";


interface IManageMenuFormDialog extends IMultiDialogChildren {
  manageMenuApi: ICallAPIFunction<IMenuFormData>;
  orphanedMenuItemsAPI?: ICallAPIFunction<IMenuFormData>;
  isCreate: boolean;
  itemData?: IMenuData;
  menuItemsList: IMenuItemBody[];
  onClose: () => void;
  updateDialogData: () => void,
  subcategories: ISubCategory[],
  menuTiming: MenuTimingArray,
  dispatch: any,
  updateData?: (updatedItems: IMenuItemBody[], updatedMenus?: IMenuData[]) => void;
  updateMenusListAPI?: any;
  enableSpecificMenuUrl?:boolean;
}
export function ManageMenuFormDialog(props: IManageMenuFormDialog) {
  const {
    onClose,
    manageMenuApi,
    venueId,
    itemData,
    isNewItem,
    menuItemsList,
    updateDialogData,
    subcategories,
    orphanedMenuItemsAPI,
    updateData,
    onDialogClose,
    updateMenusListAPI,
    enableSpecificMenuUrl
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [formRows, setFormRows] = useState<IDynamicFormRow[] | undefined>(undefined);
  const timezones = useSelector((state: IReduxAppState) => state.timezones);
  const timeZoneID = useSelector((state: IReduxAppState) => state.venue[venueId]?.venueSettings?.time_zone_id);
  const venue = useSelector((state: IReduxAppState) => state.venue[venueId]?.venueData);
  const timeZone: { name: string } | undefined = timezones?.find(timezone => timezone.id === timeZoneID);
  const { t } = useTranslation();
  const { callAPI } = useAPIV2(APIExtensions.menus, {
    onMountDisabled: true,
    yoelloAPIParams: { size: 200, include_disabled: true },
    isPaginated: true
  });
  const { hasFormChanged } = useChangedDialog();
  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.MENU_MANAGEMENT);

  const { formData, setProps, getProps, goToNextStep, goToPrevStep, closeAllModals } = useDialogSetup(props, callAPI, "itemData");

  const [menuTiming, setMenuTiming] = useState<MenuTimingArray>(
    getProps(props.dialogKey, "menuTiming") !== undefined ? getProps(props.dialogKey, "menuTiming") : itemData?.timings ?? menuTimingInitialState
  );
  const [menuTimingEnabled, setMenuTimingEnabled] = useState<boolean>(
    getProps(props.dialogKey, "menuTimingEnabled") !== undefined ? getProps(props.dialogKey, "menuTimingEnabled") : itemData?.timings_enabled ?? false
  );
  const [selectedMenuItems, setSelectedMenuItems] = useState<IMenuItemBody[]>([]);
  const [formState, setFormState] = useState();
  const { sendSnackbarMessage } = useSnackbarMessages()!;
  const { increaseDoneTasks } = useTask();

  useEffect(() => {
    if (!formData && !isNewItem) return;
    async function setUpData() {
      let times = menuTimingInitialState;
      setSelectedMenuItems(getProps(props.dialogKey, "selectedMenuItems") ??
        getProps(props.dialogKey, "selectedMenuItemIDs", "itemData") ?? []);

      if (!isNewItem && !getProps(props.dialogKey, "selectedMenuItemIDs", "itemData")) {
        setIsLoading(true)
        /**Gets menu items for menu data */
        const result = await callAPI(HTTPMethods.GET, {
          query: `${itemData?.id ?? props.id}/menu_items`,

        });
        let items: IMenuItemBody[] = result.data as IMenuItemBody[];
        const ids = items.map((item) => item.id);
        // eslint-disable-next-line
        items = [...items.filter((item: IMenuItemBody) => {
          if (item.pricing_options && Object.values(item.pricing_options).length > 0) return item
        })]
        setSelectedMenuItems(items);
        setProps(props.dialogKey, "selectedMenuItemIDs", ids, "itemData")
        setProps(props.dialogKey, "selectedMenuItems", items);


        if (props.id) {
          const res = await callAPI(HTTPMethods.GET, {
            query: `${props.id}/timings?venue_id=` + venueId
          });
          if (res.isSuccessful) {
            times = generateMenuTimingArray(res.data as menuTimingList, timeZone?.name);
            setMenuTiming(times);
            setProps(props.dialogKey, "menuTiming", times);
            setProps(props.dialogKey, "oldmenuTiming", times);

          }
        }
        setMenuTimingEnabled(formData?.timings_enabled);
        setFormState({
          ...formData,
          selectedMenuItemIDs: ids,
          menuTiming: times
        })
      } else if (!isNewItem) {
        // hasFormChanged(true);
        setFormState({
          ...formData,
          menuTiming: getProps(props.dialogKey, "oldMenuTiming") ?? menuTimingInitialState
        })
      } else {
        setFormState(formData ?? {
          menuTiming: times,
          description: '',
          enabled: false,
          name_public: '',
          name_private: '',
          selectedMenuItemIDs: []
        });
      }
      setIsLoading(false)
    }
    setUpData();
    //eslint-disable-next-line
  }, [isNewItem, formData]);

  useEffect(() => {
    const rows: IDynamicFormRow[] =
      [
        ...manageMenuForm({ ...props, setProps, getProps, goToNextStep, goToPrevStep }, menuItemsList, isLoading,
          onSecondDialogSubmit, selectedMenuItems, onDeleteSelectedItem, manageMenuApi, props.isNewItem, t, subcategories, closeAllModals,
          itemData,onFailedInteraction),
        {
          label: t("manageMenu.form.MenuTiming"),
          keyName: "menuTiming",
          Component: TimingMenusContainer,
          formProps: {
            secondLabel: t("manageMenu.form.MenuTimingLabel"),
            menuTiming: menuTiming,
            setMenuTiming: setMenuTiming,
            updateDialogData: updateDialogData,
            enabled: menuTimingEnabled,
            setMenuTimingEnabled: setMenuTimingEnabled
          }
        },
      ];
      if(enableSpecificMenuUrl){
        const menuURL = `${generateConsumerURL()}/v/${venue?.venue_nickname}?menu_id=${itemData?.id ?? props.id}`
        rows.push({
            label: "manageMenu.form.menuURL",
            keyName: "menu_url",
            Component:MenuURLLookup,
            formProps:{
              menuURL
            }
        })
      }  
    setFormRows(rows);
    setProps(props.dialogKey, "menuTiming", menuTiming);
    setProps(props.dialogKey, "menuTimingEnabled", menuTimingEnabled);
    // setProps(props.dialogKey, "selectedMenuItemIDs", selectedMenuItems);
    // eslint-disable-next-line
  }, [menuTiming, menuTimingEnabled, selectedMenuItems])

  async function onSubmit(data: Partial<IMenuFormData>) {
    // @ts-ignore
    if (selectedMenuItems.length === 0 && data.enabled) {
      sendSnackbarMessage(t("manageMenu.Errors.AddItemsError"), "error");
      return
    }
    const { hasActiveDay, hasEmptyRow, timing_data }: TimingData = generateMenuTimingData(menuTiming, venueId, undefined, timeZone?.name);
    if (hasEmptyRow) {
      sendSnackbarMessage(t("manageMenu.Errors.MenuTimingEmptyRow"), "error");
      return;
    }
    if (menuTimingEnabled && !hasActiveDay) {
      sendSnackbarMessage(t("manageMenu.Errors.SpesificTimingError"), "error");
      return;
    }

    if (isNewItem) {
      setIsLoading(true);
      const response = await manageMenuApi(HTTPMethods.POST, {
        body: {
          "menu_data": {
            name_public: data.name_public,
            name_private: data.name_private,
            venue_id: venueId,
            timings_enabled: menuTimingEnabled,
            enabled: data.enabled,
            description: data.description
          },
          // @ts-ignore
          'menu_item_data': selectedMenuItems.length > 0 ? selectedMenuItems.map(item => item.id) : undefined,
          'timing_data': timing_data
        },
      });
      if (response.isSuccessful) {
        sendSnackbarMessage(t("manageMenu.Errors.CreateSuccess"), "success");
        increaseDoneTasks();
        if (orphanedMenuItemsAPI) {
          orphanedMenuItemsAPI(HTTPMethods.GET).then(res => {
            if (updateMenusListAPI) {
              updateMenusListAPI(HTTPMethods.GET)
            } else {
              manageMenuApi(HTTPMethods.GET).then(resMenus => {
                if (updateData) {
                  updateData(res.data as unknown as IMenuItemBody[], resMenus.data as unknown as IMenuData[])
                }
              })
            }


          })

        }
        if (onDialogClose) {
          onDialogClose();
        } else {
          onClose();
        }
      } else {
        sendSnackbarMessage(t("manageMenu.Errors.CreateFail"), "error");
      }
      setIsLoading(false);
    } else {
      const id = itemData?.id ?? props.id;
      setIsLoading(true);
      const response = await manageMenuApi(HTTPMethods.PATCH, {
        query: id,
        body: {
          ...data,
          timings_enabled: menuTimingEnabled
        },
      });
      const secondResponse = await callAPI(HTTPMethods.PUT, {
        query: id + "/menu_items",
        // @ts-ignore
        body: selectedMenuItems.length > 0 ? selectedMenuItems.map(item => { return { "id": item.id } }) : [],
      });
      if (response.isSuccessful && secondResponse.isSuccessful) {
        const data = response.data as IMenuData;
        const menuTimingApiResponse = await callAPI(HTTPMethods.PUT, {
          query: `${id}/timings?venue_id=` + venueId,
          body: generateMenuTimingData(menuTiming, venueId, data.id, timeZone?.name).timing_data
        });
        if (menuTimingApiResponse.isSuccessful) {
          sendSnackbarMessage(t("manageMenu.Errors.UpdateSuccess"), "success");

          if (onDialogClose) {
            onDialogClose();
          } else {
            onClose();
          }
        } else {
          sendSnackbarMessage("manageMenu.Errors.UpdateTimingFail", "error");
          setIsLoading(false);
        }
      } else {
        sendSnackbarMessage(t("manageMenu.Errors.UpdateFail"), "error");
        setIsLoading(false);
      }
    }
  }
  function onSecondDialogSubmit(selectedMenuItemsList: IMenuItemBody[], changedMenuItemsList: IMenuItemBody[]) {
    setSelectedMenuItems(selectedMenuItemsList);
    setProps(props.dialogKey, "selectedMenuItems", selectedMenuItemsList);
    // onClose()
  }

  function onDeleteSelectedItem(id: string) {
    setSelectedMenuItems(selectedMenuItems.filter(item => item.id !== id));
  }

  return (!formRows || !formState || isLoading ?  <FormLoadingWrapper> <ListLoading/>  </FormLoadingWrapper> :
    <DisabledActionWrapper
      disabled={!hasPermission}
      onDisabledClick={() => onFailedInteraction?.()}
    >
      <DynamicForm
        useFormOptions={{
          mode: "onBlur",
        }}
        onHandleSubmit={onSubmit}
        formRows={formRows!}
        formData={formState}
        formKeyName={"ManageMenu"}
        isDialog={true}
        disableAll={!hasPermission}
        disableAllInteraction={onFailedInteraction}
        hasDataChanged={hasFormChanged}
        formButtons={{
          leftButton: {
            type: "button",
            label: t("forms.buttons.cancel"),
            color: EColourPanel.PRIMARY,
            fullWidth: false,
            onClick: onClose,
          },
          rightButton: {
            type: "submit",
            label: isNewItem ? t("manageMenu.buttons.createMenu") : t("manageMenu.buttons.updateMenu"),
            color: EColourPanel.PRIMARY,
            fullWidth: false,
            disabled: isLoading || !hasPermission
          },
        }}
      />
    </DisabledActionWrapper>

  );
}
